---
title: Terms of Service
---

<script lang="ts">
	import LegalContact from '$lib/components/LegalContact.svelte';
	import TermsOfServiceIntro from "$lib/components/TermsOfServiceIntro.svelte";

	export let data;
</script>

<style>
  :global(h2) {
    margin-top: 1rem !important;
  }

  :global(h3) {
    margin-top: 1rem !important;
  }
</style>

# Terms of Service

Our Terms of Service were last updated on September 7, 2025.

<TermsOfServiceIntro config={data.config} />

Please read them carefully before using our website or purchasing our services and products.

## Article 1: General

By using this website (the "Site"), you agree to be bound by these Terms of Service and to use the Site in accordance with these Terms, our Privacy Policy, and any additional terms and conditions that may apply to specific sections of the Site or to products and services available through the Site from Artificer Innovations, LLC ("Company", "we", "our", or "us").

Accessing the Site, in any manner, whether automated or otherwise, constitutes use of the Site and your agreement to be bound by these Terms of Service.

### Article 1a: Age Limit

This Site is not directed to children under the age of 13, and we do not knowingly collect personal information from children under 13.

## Article 2: Services and Products

### Consulting Services

Artificer Innovations provides advisory and consulting services. By engaging our services, you agree to:

- Provide accurate and complete information necessary for service delivery
- Comply with all applicable laws and regulations
- Respect confidentiality agreements and intellectual property rights
- Pay all fees as agreed upon in our service agreements

### Digital Products

We offer digital products including but not limited to the 100x Developers Playbook and other educational materials. By purchasing digital products, you agree to:

- Use the products for personal or authorized business purposes only
- Not redistribute, resell, or share access to the products
- Respect all copyright and intellectual property rights

## Article 3: Placing Orders

By placing an order for digital products through the Site, you warrant that you are legally capable of entering into binding contracts and that you are at least 18 years old.

## Article 4: Pricing and Payment

### Pricing

All prices are indicated in U.S. dollars. Prices for services and products may be changed at any time; however, the price applied to an order will be the one stated at the time of purchase or service agreement.

### Payment Terms

- **Digital Products**: Payment is due immediately at the time of order
- **Consulting Services**: Payment terms are specified in individual service agreements
- **Pre-orders**: Payment is due at the time of pre-order placement

Payment can be made through any of the payment methods we have available, such as Visa, MasterCard, American Express, or online payment methods (for example, PayPal).

Payment cards (credit or debit) are subject to validation checks and authorization by your card issuer. If we do not receive the required authorization, we will not be liable for failure to complete your order or provide access to the digital product.

## Article 5: Your Information

If you wish to place an order for digital products or engage our consulting services, you may be asked to supply certain information relevant to your order or engagement, including, without limitation, your name, email address, billing address, and payment information.

You represent and warrant that:
- You have the legal right to use any credit or debit card(s) or any other payment method(s) in connection with any order
- The information you supply to us is true, correct, and complete
- You will maintain the accuracy of such information

By submitting such information, you grant us the right to provide the information to payment processing third parties for purposes of facilitating the completion of your order.

## Article 6: Delivery and Access

### Digital Products

Delivery of digital products is made electronically, either through direct download from the Site or to the email address you provide at the time of purchase. Please ensure your email address is accurate to avoid delivery issues.

Once access to the digital product has been provided, the responsibility for downloading, storing, and securing the product is yours. If you experience any difficulty accessing your purchase, please contact us at [contact@artificerinnovations.com](mailto:contact@artificerinnovations.com) and we will assist you in receiving your product.

### Consulting Services

Service delivery terms are specified in individual service agreements. We will work with you to establish appropriate timelines and deliverables for each engagement.

## Article 7: Intellectual Property

All content on this website, including but not limited to text, graphics, logos, images, and software, is the property of Artificer Innovations, LLC or its content suppliers and is protected by copyright and other intellectual property laws.

Our digital products are protected by copyright and other intellectual property rights. You may not:
- Copy, distribute, or resell our digital products
- Share access credentials with others
- Use our content for commercial purposes without written permission

## Article 8: Limitation of Liability

To the fullest extent permitted by law, Artificer Innovations, LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the Site or our services and products.

## Article 9: Changes to These Terms

We may update these Terms of Service from time to time. Any changes will be effective immediately upon posting on the Site. Your continued use of the Site after changes are posted constitutes your acceptance of the updated Terms.

We will notify users of material changes via email or through a notice on our website.

## Article 10: Governing Law

These Terms of Service shall be governed by and construed in accordance with the laws of the State of Washington, without regard to its conflict of law provisions.

## Article 11: Contact Information

<LegalContact config={data.config} />

---

*These Terms of Service are effective as of September 7, 2025, and apply to all users of our website and purchasers of our services and products.*
